import { intKeyedEntries, intKeys } from '@/helpers/javascript'

export class NodeTraversal {
	readonly #links: Map<number, Set<number>>
	readonly #nodes: Set<number>

	constructor (rawLinks: Record<number, Record<number, any[]>>, rawNodes: Record<number, any>) {
		this.#nodes = new Set(intKeys(rawNodes))
		this.#links = new Map()
		intKeyedEntries(rawLinks).forEach(([lesser, greaters]) =>
			intKeyedEntries(greaters).forEach(([greater, __]) => {
				if (!this.#links.has(lesser)) {
					this.#links.set(lesser, new Set())
				}
				this.#links.get(lesser)!!.add(greater)
				if (!this.#links.has(greater)) {
					this.#links.set(greater, new Set())
				}
				this.#links.get(greater)!!.add(lesser)
			}))
	}

	neighbors (id0: number): number[] {
		const links = this.#links.get(id0)
		if (links) {
			return [ ...links ]
		} else {
			return []
		}
	}

	/**
	 * Hop count from `root` to every node reachable from it. Unlike the subgraph helpers below this
	 * leaves the traversal untouched, so the result can drive layout on every tick.
	 */
	depths (root: number): Map<number, number> {
		const depths = new Map<number, number>()
		if (!this.#nodes.has(root)) {
			return depths
		}
		depths.set(root, 0)
		let frontier = [ root ]
		let depth = 0
		while (frontier.length) {
			depth++
			const next: number[] = []
			for (const id0 of frontier) {
				for (const neighbor of this.#links.get(id0) ?? []) {
					if (!depths.has(neighbor)) {
						depths.set(neighbor, depth)
						next.push(neighbor)
					}
				}
			}
			frontier = next
		}
		return depths
	}

	delete (id0: number) {
		this.#nodes.delete(id0)
		const neighbors = this.#links.get(id0)
		this.#links.delete(id0)
		if (neighbors) {
			for (const neighbor of neighbors) {
				this.#links.get(neighbor)!!.delete(id0)
				if (!this.#links.get(neighbor)?.size) {
					this.#links.delete(neighbor)
				}
			}
		}
	}

	splitByNodeId (id0: number): SplitResult {
		const inner = this.subgraphByNodeId(id0)
		const outer = [ ...this.#nodes ]
		return { inner, outer }
	}

	subgraphByNodeId (id0: number): number[] {
		this.#nodes.delete(id0)
		if (!this.#links.has(id0)) {
			return []
		}
		const neighbors = this.#links.get(id0)!!.intersection(this.#nodes)
		this.#links.delete(id0)
		for (const neighbor of neighbors) {
			this.#links.get(neighbor)!!.delete(id0)
			if (!this.#links.get(neighbor)?.size) {
				this.#links.delete(neighbor)
			}
		}
		return [ ...neighbors ].map(n => this.subgraphByNodeId(n)).flat()
	}
}

interface SplitResult {
	inner: number[]
	outer: number[]
}
